import { mutateRefreshToken } from '@server/index'
import { ITokens } from '@/types/ITokens'

/**
 * Limpa os tokens de acesso e atualização do cache.
 */
export const clearTokens = () => {
  const tokens = useCookie('tokens')
  tokens.value = null
}

/**
 * Salva os tokens de acesso e atualização encriptados.
 * @param tokens Os tokens de acesso e atualização.
 */
export const saveTokens = (tokens: ITokens) => {
  const tokensCookie = useCookie('tokens')
  tokensCookie.value = encryptData(tokens)
}

/**
 * Verifica se o refreshToken está expirado
 * @param tokens Os tokens de acesso e atualização.
 * @returns verdadeiro caso expirado, falso caso ainda valido
 */
function isRefreshTokenExpired(token: ITokens): boolean {
  try {
    if (token.refresh == null) {
      return true
    }
    const [, payloadBase64] = token.refresh.split('.')

    if (!payloadBase64) {
      return true
    }

    const payloadJson = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(payloadJson)

    if (typeof payload.exp !== 'number') {
      return true
    }

    const currentTimeInSeconds = Math.floor(Date.now() / 1000)
    return currentTimeInSeconds > payload.exp
  } catch {
    return true
  }
}

/**
 * Retorna os tokens de acesso e atualização desencriptados.
 * @returns Os tokens desencriptados.
 */
export const getTokens = (): ITokens | null => {
  const tokensCookie = useCookie('tokens')

  if (!tokensCookie.value) {
    return null
  }

  const tokens = decryptData(tokensCookie.value) as ITokens
  if (tokens == null || isRefreshTokenExpired(tokens)) return null
  return tokens
}

/**
 * Atualiza o token de acesso utilizando o token de atualização.
 * @returns Uma Promise que resolve com o novo token de acesso.
 */
export const refreshAccessToken = (): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const tokens = getTokens()

    if (!tokens?.refresh) {
      return reject(new Error('[Error Apollo] no refresh token available'))
    }

    const { execute, data, error } = mutateRefreshToken({
      refreshToken: tokens.refresh
    })

    execute()
      .then(() => {
        if (data.value) {
          saveTokens({
            access: data.value.accessToken,
            refresh: data.value.refreshToken
          })
          resolve(data.value.accessToken)
        } else {
          reject(new Error(error.value || '[Error Apollo] Failed to refresh token'))
        }
      })
      .catch((err: Error) => {
        reject(err)
      })
  })
}
